// v3 stonemason, authored the way age-of-mcp writes it: local field helpers +
// the shared styleV3 part makers, instead of a flat wall of literals. An open
// stone WORK YARD: an oversized rough block being dressed on an open cutting
// bench is the icon, with a big upright grindstone wheel beside it and a stack
// of oversized freshly-dressed blocks in the east yard; a small slate-roofed
// (ROOF_MINERAL) half-shed in the back-west corner shelters finished blocks
// only. Ported 1:1 from age-of-mcp's v3/stonemason.ts.
import { defineModel, benchWork, styleV3, type Part } from "../../lib";

const { box, cyl, poles, slab, ROOF_MINERAL, STONE, STONE_DK, TIMBER, WOOD } = styleV3;

const BASE = 0.12;           // foundation slab top

// The open cutting bench in the middle of the yard.
function bench(): Part {
  return box(0.62, 0.16, 0.4, WOOD, [0.06, BASE + 0.18, 0.08]);
}

// The oversized rough block being dressed — the icon from above.
function bigBlock(): Part {
  return box(0.4, 0.3, 0.3, STONE, [0.06, BASE + 0.41, 0.08], [0, 0.06, 0]);
}

// A big upright grindstone wheel on a post frame, in the open.
function grindstone(): Part[] {
  return [
    cyl(0.2, 0.2, 0.07, 12, STONE_DK, [-0.34, BASE + 0.26, 0.3], [Math.PI / 2, 0, 0]),
    box(0.07, 0.26, 0.07, TIMBER, [-0.34, BASE + 0.13, 0.3]),
  ];
}

// Oversized dressed blocks stacked in the east yard.
function dressedBlocks(): Part[] {
  return [
    box(0.26, 0.2, 0.26,  STONE,    [0.32, BASE + 0.1, -0.26]),
    box(0.22, 0.16, 0.22, STONE_DK, [0.32, BASE + 0.28, -0.26], [0, 0.3, 0]),
    box(0.22, 0.16, 0.22, STONE,    [0.36, BASE + 0.08, 0.34], [0, -0.2, 0]),
  ];
}

// Four posts of the back-west storage shed (the roof comes with stage 3).
function shedPosts(): Part[] {
  const cx = -0.3, cz = -0.3;
  const out: Part[] = [];
  for (const [x, z] of [[-0.16, -0.16], [0.16, -0.16], [-0.16, 0.16], [0.16, 0.16]] as const) {
    out.push(cyl(0.028, 0.028, 0.5, 5, TIMBER, [cx + x, BASE + 0.25, cz + z]));
  }
  return out;
}

// The slate shed roof — the v3 mineral-industry code, over storage only.
function shedRoof(): Part {
  return box(0.46, 0.05, 0.46, ROOF_MINERAL, [-0.3, BASE + 0.53, -0.3], [0.16, 0, 0]);
}

// Finished blocks kept under the shed.
function storedBlocks(): Part[] {
  return [
    box(0.2, 0.12, 0.2, STONE,    [-0.3, BASE + 0.06, -0.3]),
    box(0.17, 0.1, 0.17, STONE_DK, [-0.3, BASE + 0.17, -0.3], [0, 0.2, 0]),
  ];
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(0.9, 0.9)];
  if (s === 1) return [slab(0.9, 0.9), bench(), ...storedBlocks(), ...poles(0.84, 0.84, 0.55)];
  if (s === 2) {
    return [
      slab(0.9, 0.9), bench(), bigBlock(), ...grindstone(),
      ...shedPosts(), ...storedBlocks(), ...poles(0.84, 0.84, 0.9),
    ];
  }
  return [
    slab(0.9, 0.9), bench(), bigBlock(), ...grindstone(),
    ...shedPosts(), shedRoof(), ...storedBlocks(), ...dressedBlocks(),
  ];
}

export const stonemasonV3 = defineModel({
  id: "stonemason-v3",
  tiles: { w: 2, h: 2 },
  batchId: "45-optimized",
  name: "Stonemason (v3)",
  tags: ["building","stonemason","low-poly","age-of-mcp","v3"],
  summary: "Low-poly stonemason.",
  description: "A mason's cutting bench that cuts rough stone into dressed blocks.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
  stations: [
    { id: "bench-a", position: [0.06, 0.12, 0.5], facing: Math.PI },
    { id: "bench-b", position: [0.18, 0.12, -0.36], facing: 0 },
  ],
  interactions: [
    { id: "carve", label: "Carving stone", station: "bench-a", variants: ["complete"], ...benchWork() },
  ],
});
